import { Button, type ButtonProps, CircularProgress } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';
import { spacing, transitions } from '@/theme/tokens';
import { useBookingTokens } from '@/theme/useBookingTokens';

type AppButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type AppButtonSize = 'sm' | 'md' | 'lg';

interface AppButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {
  variant?: AppButtonVariant;
  size?: AppButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const sizeStyles = {
  sm: {
    padding: `${spacing[2]} ${spacing[4]}`,
    fontSize: '13px',
    minHeight: '32px',
  },
  md: {
    padding: `${spacing[3]} ${spacing[6]}`,
    fontSize: '14px',
    minHeight: '40px',
  },
  lg: {
    padding: `${spacing[4]} ${spacing[8]}`,
    fontSize: '15px',
    minHeight: '48px',
  },
};

function buildVariantStyles(colors: ReturnType<typeof useBookingTokens>['colors']) {
  return {
    primary: {
      backgroundColor: colors.brass,
      color: colors.textPrimary,
      border: 'none',
      '&:hover': {
        backgroundColor: colors.brassHover,
      },
      '&:active': {
        backgroundColor: colors.brassHover,
      },
      '&:disabled': {
        backgroundColor: colors.border,
        color: colors.textMuted,
      },
    },
    secondary: {
      backgroundColor: colors.white,
      color: colors.accent,
      border: `1px solid ${colors.accent}`,
      '&:hover': {
        borderColor: colors.accentHover,
        backgroundColor: colors.accentLighter,
      },
      '&:active': {
        backgroundColor: colors.accentLight,
      },
      '&:disabled': {
        borderColor: colors.border,
        color: colors.textMuted,
        backgroundColor: colors.white,
      },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: colors.accent,
      border: 'none',
      '&:hover': {
        backgroundColor: colors.accentLighter,
      },
      '&:active': {
        backgroundColor: colors.accentLight,
      },
      '&:disabled': {
        color: colors.textMuted,
      },
    },
    danger: {
      backgroundColor: colors.danger,
      color: colors.white,
      border: 'none',
      '&:hover': {
        backgroundColor: '#C75D5D',
      },
      '&:active': {
        backgroundColor: '#B65252',
      },
      '&:disabled': {
        backgroundColor: colors.border,
        color: colors.textMuted,
      },
    },
  };
}

export const AppButton = forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      sx,
      ...props
    },
    ref
  ) => {
    const { colors, radius } = useBookingTokens();
    const variantStyles = buildVariantStyles(colors);
    return (
      <Button
        ref={ref}
        disabled={disabled || loading}
        sx={{
          ...sizeStyles[size],
          ...variantStyles[variant],
          borderRadius: radius.md,
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: `all ${transitions.fast}`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: spacing[2],
          '&:hover': {
            ...variantStyles[variant]['&:hover'],
            boxShadow: 'none',
          },
          ...sx,
        }}
        {...props}
      >
        {loading ? (
          <CircularProgress
            size={16}
            sx={{ color: variant === 'primary' ? colors.bg : colors.textPrimary }}
          />
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </Button>
    );
  }
);

AppButton.displayName = 'AppButton';
